import { FC } from 'react';
import Link from 'next/link';
import { Block } from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Button from '../Button';
import { CType } from '../../models/Contentful/types';
import { IChallengeProps } from '../../models/Challenge/@types';
import ImageComponent from '../Image';
import SvgLib from '../SvgLib';
import Typo from '../Typo';
import VideoPlayer from '../VideoPlayer';
import { getChallengeUrl } from '../../models/Challenge';
import styles from './featuredChallenge.module.sass';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';

const ChallengeCard: FC<CType<IChallengeProps>> = props => {
  const { fields } = props;
  const { title, description, media, image, videoUrl, earnPoints, award, partners, launchDate } =
    fields;
  const { isMobile } = useIsMobileViewport();
  const charactersAllowed = isMobile ? 60 : 78;
  const descriptionText = documentToPlainTextString(description as unknown as Block);
  let descriptionUptoLimit = descriptionText;
  if (descriptionText.length > charactersAllowed) {
    descriptionUptoLimit = descriptionText.substring(0, charactersAllowed);
  }

  const challengeUrl = getChallengeUrl(fields);
  const withLink = (child: React.ReactNode) => <Link href={challengeUrl}>{child}</Link>;

  // eslint-disable-next-line react/no-unstable-nested-components
  const Description = () => {
    return (
      <div className={`${styles.lineClamp}`}>
        <Typo normal limitLines={2} classes={`is-color-Black ${styles.description}`}>
          {descriptionUptoLimit}
          {descriptionUptoLimit.length < descriptionText?.length && (
            <>
              <span>... </span>
              {withLink(
                <Typo normal classes="is-color-Blue">
                  More
                </Typo>,
              )}
            </>
          )}
        </Typo>
      </div>
    );
  };

  return (
    <div className={`${styles.challengeCard} has-text-centered is-flex is-flex-direction-column`}>
      {launchDate && new Date(launchDate) > new Date() && (
        <div className={`is-bgcolor-HighLightLime ${styles.launchType}`}>
          <Typo small>Coming Soon</Typo>
        </div>
      )}
      <div className={styles.challengeMedia}>
        {media === 'Video' && videoUrl ? (
          <VideoPlayer videoUrl={videoUrl} classes={styles.videoPlayer} aspectRatio="16 / 9" />
        ) : (
          <ImageComponent image={image} height="193px" aspectRatio="16 / 9" />
        )}
      </div>
      {/* INFO PART */}
      <div className={styles.challengeInfoContainer}>
        <div className={styles.challengeInfo}>
          <div className="is-flex is-flex-direction-column">
            <Typo h2 limitLines={2} classes={`font-weight-bold is-size-3 ${styles.title}`}>
              {title}
            </Typo>
            {!isMobile && !!descriptionText && <Description />}
          </div>
          {partners[0] && (
            <ImageComponent
              image={partners[0].fields.image}
              width={isMobile ? '85px' : '100px'}
              height="40px"
              classes={isMobile ? '' : 'ml-5'}
            />
          )}
        </div>
        {/* Points part */}
        <div
          className={`is-flex is-flex-direction-row has-text-left is-color-Black ${styles.challengePoints} `}
        >
          <div className="is-flex is-flex-direction-column">
            <div className="is-flex is-align-items-center">
              <Typo normal>Earns</Typo>
            </div>
            <div className="is-flex is-align-items-center mt-1">
              <SvgLib icon="earned" iconSize="Medium" />
              <Typo h3>{`+${earnPoints} Points`}</Typo>
            </div>
          </div>
          {award && (
            <div className="is-flex is-flex-direction-column">
              <div className="is-flex is-align-items-center">
                <Typo normal>Winner</Typo>
              </div>
              <div className="is-flex is-align-items-center mt-1">
                <SvgLib icon="winner" iconSize="Medium" />
                <Typo h3 classes="ml-2">
                  {award}
                </Typo>
              </div>
            </div>
          )}
        </div>
        {isMobile && !!descriptionText && <Description />}

        {withLink(
          <Button classes={styles.button} theme="Filled - Lime">
            View Challenge
          </Button>,
        )}
      </div>
    </div>
  );
};

export default ChallengeCard;
